import { DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { FC } from 'react';
import { useState } from 'react';

import { PictureComponent } from 'app/components/commons/PictureComponent/PictureComponent';
import type { Picture } from 'app/typings';

import './PictureListItem.scss';

type PictureListItemProps = {
  item: Picture;
  onPreview?: () => void;
  onRemove?: () => void;
  onEdit?: () => void;
  draggable?: boolean;
  id?: string;
};

export const PictureListItem: FC<PictureListItemProps> = ({
  item,
  onPreview,
  onRemove,
  onEdit,
  draggable = false,
  id,
}) => {
  const [isHover, setIsHover] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: id || item.pictureId,
    disabled: !draggable,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleMouseEnter = () => setIsHover(true);
  const handleMouseLeave = () => setIsHover(false);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`picture-list-item ${
        draggable ? 'picture-list-item-sortable' : ''
      } ${isDragging ? 'picture-list-item-dragging' : ''}`}
      {...(draggable ? { ...attributes, ...listeners } : {})}
    >
      <div className="picture-list-item__thumbnail">
        <PictureComponent pictureId={item.pictureId} alt={item.alt} />
      </div>
      <div
        onMouseLeave={handleMouseLeave}
        onMouseEnter={handleMouseEnter}
        className="picture-list-item__hover-area"
      >
        {isHover && (
          <div className="picture-list-item__hover-buttons">
            {onPreview && (
              <EyeOutlined
                onClick={(e) => {
                  e.stopPropagation();
                  onPreview();
                }}
              />
            )}
            {onEdit && (
              <EditOutlined
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
              />
            )}
            {onRemove && (
              <DeleteOutlined
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
